/**
 * AP-32 7.3 — **Der Rechenkern des Belegs.** Rein, ohne Datenbank.
 *
 * Er liegt in `shared-types` und nicht im Backend, weil das Positionsraster den
 * Zeilenbetrag sofort neu rechnet (13.3): Zwei Rechenwege wären die erste
 * Abweichung zwischen Bildschirm und Beleg — dieselbe Überlegung wie bei
 * `erfuelltRecht` (Entscheidung 19.2-29).
 *
 * Gerechnet wird **in ganzen Cent mit `bigint`**, nie in Gleitkomma: Eine Menge
 * mit drei und ein Preis mit zwei Nachkommastellen, geteilt durch die
 * Preiseinheit und mit einem Rabatt versehen, ergibt Zwischenwerte, an denen
 * `0.1 + 0.2` scheitert. Gerundet wird genau einmal, kaufmännisch (halb von der
 * Null weg), und **die Division durch die Preiseinheit steht vor der Rundung** —
 * sonst wäre sie wirkungslos (4.4.1).
 *
 * Die Regel, die alles zusammenhält (5.3):
 *
 * > **Ein Betrag zählt genau einmal.** Im Modus `SUM_OF_CHILDREN` zahlen die
 * > Kinder ein und der Elternbetrag ist ihre Anzeige; im Modus `OWN_PRICE` zahlt
 * > der Elternbetrag ein und die Kinder sind Text.
 */

import type { DocumentLineType, PriceMode, SubPositionMode, TaxProcedure } from './documents'

// ─── Ganzzahlarithmetik ──────────────────────────────────────────────────────

/**
 * Ein Wert mit höchstens `stellen` Nachkommastellen als ganze Zahl.
 * Die Eingänge sind vorher auf ihre Stellenzahl geprüft; `value × 10^stellen`
 * liegt dann höchstens um Rundungsrauschen neben einer ganzen Zahl.
 */
export function skaliert(wert: number, stellen: number): bigint {
  if (!Number.isFinite(wert)) return 0n
  return BigInt(Math.round(wert * 10 ** stellen))
}

/** Ganzzahlige Division, kaufmännisch gerundet (halb von der Null weg). */
export function teileGerundet(zaehler: bigint, nenner: bigint): bigint {
  if (nenner === 0n) return 0n
  const negativ = zaehler < 0n !== nenner < 0n
  const z = zaehler < 0n ? -zaehler : zaehler
  const n = nenner < 0n ? -nenner : nenner
  const q = (z * 2n + n) / (2n * n)
  return negativ ? -q : q
}

/** Cent → Euro als Zahl mit zwei Nachkommastellen. */
export function centZuBetrag(cent: bigint): number {
  return Number(cent) / 100
}

// ─── Eingang und Ergebnis ────────────────────────────────────────────────────

export interface CalcLineInput {
  /** Ein stabiler Schlüssel je Zeile, damit das Ergebnis zugeordnet werden kann. */
  key: string
  parentKey: string | null
  lineType: DocumentLineType
  /** Drei Nachkommastellen. */
  quantity: number
  /** Zwei Nachkommastellen. Bei `priceMode = GROSS` ist das der Bruttopreis. */
  unitPrice: number
  /** Ganze Zahl 1–1.000.000 (`istGueltigePreiseinheit`). */
  priceUnit: number
  /** Prozent, zwei Nachkommastellen. */
  discountPercent: number | null
  /** Der Satz der Zeile — das Verfahren des Belegs kann ihn auf 0 setzen (7.2). */
  taxRate: number
  subMode: SubPositionMode | null
  isOptional: boolean
  isAlternative: boolean
}

export interface CalcInput {
  /** In Belegreihenfolge — Eltern vor ihren Kindern. */
  lines: readonly CalcLineInput[]
  priceMode: PriceMode
  taxProcedure: TaxProcedure
  /** Belegrabatt in Prozent auf die Summe der wertführenden Zeilen. */
  documentDiscountPercent: number | null
}

export interface CalcLineResult {
  key: string
  positionNo: number
  subPositionNo: number | null
  countsToTotal: boolean
  /** Der Satz, der tatsächlich gilt — 0, wenn das Verfahren nicht `STANDARD` ist. */
  effectiveTaxRate: number
  lineNet: number
  lineTax: number
  lineGross: number
  /** Die Zeile trägt keine Beträge (Text, Titel, Kind im Modus `OWN_PRICE`). */
  withoutAmounts: boolean
  /** Nur `DEDUCTION`: die Zwischensumme, auf die der Abzug wirkt. */
  deductionBase: number | null
}

export interface CalcTaxGroup {
  taxRate: number
  taxProcedure: TaxProcedure
  netAmount: number
  taxAmount: number
}

export interface CalcResult {
  lines: CalcLineResult[]
  taxes: CalcTaxGroup[]
  /** Summe der wertführenden Zeilen, **vor** dem Belegrabatt. */
  netTotal: number
  /** Betrag des Belegrabatts (positiv bei einem Nachlass). */
  discountTotal: number
  taxTotal: number
  /** netTotal − discountTotal + taxTotal. */
  grossTotal: number
}

// ─── Eine Zeile ──────────────────────────────────────────────────────────────

/**
 * Der Zeilenbetrag in Cent: `Menge × Einzelpreis ÷ Preiseinheit × (1 − Rabatt%)`,
 * einmal gerundet. Bei `GROSS` ist das Ergebnis brutto.
 *
 * 250 Stück zu 0,35 € je 100 → 250 × 35 ÷ 100 = 87,5 Cent → **0,88 €**.
 */
export function zeilenbetragCent(
  quantity: number,
  unitPrice: number,
  priceUnit: number,
  discountPercent: number | null,
): bigint {
  const mengeMilli = skaliert(quantity, 3)
  const preisCent = skaliert(unitPrice, 2)
  const rabattBp = skaliert(discountPercent ?? 0, 2)
  const pe = BigInt(priceUnit > 0 ? priceUnit : 1)
  return teileGerundet(mengeMilli * preisCent * (10000n - rabattBp), 1000n * pe * 10000n)
}

/** Steuer auf einen Nettobetrag: `netto × Satz`, einmal gerundet. */
function steuerAufNetto(nettoCent: bigint, satzBp: bigint): bigint {
  return teileGerundet(nettoCent * satzBp, 10000n)
}

/** Netto aus einem Bruttobetrag herausrechnen. */
function nettoAusBrutto(bruttoCent: bigint, satzBp: bigint): bigint {
  return teileGerundet(bruttoCent * 10000n, 10000n + satzBp)
}

// ─── Der Beleg ───────────────────────────────────────────────────────────────

const BETRAGSLOSE: readonly DocumentLineType[] = ['TEXT', 'TITLE', 'PAGE_BREAK']

export function berechneBeleg(input: CalcInput): CalcResult {
  const zeilen = input.lines
  const kinderVon = new Map<string, CalcLineInput[]>()
  for (const z of zeilen) {
    if (z.parentKey) kinderVon.set(z.parentKey, [...(kinderVon.get(z.parentKey) ?? []), z])
  }
  const eltern = new Map(zeilen.map((z) => [z.key, z]))
  const standard = input.taxProcedure === 'STANDARD'
  const brutto = input.priceMode === 'GROSS'

  // Zwischenstand je Zeile in Cent.
  const betrag = new Map<string, { wert: bigint; zaehlt: boolean; ohne: boolean; satzBp: bigint }>()

  // 1. Blätter und eigenständige Zeilen — alles, was selbst einen Betrag trägt.
  for (const z of zeilen) {
    const satzBp = standard ? skaliert(z.taxRate, 2) : 0n
    const elternteil = z.parentKey ? eltern.get(z.parentKey) : undefined
    const kinder = kinderVon.get(z.key) ?? []
    const nichtVerbindlich =
      z.isOptional || z.isAlternative || !!(elternteil && (elternteil.isOptional || elternteil.isAlternative))

    if (BETRAGSLOSE.includes(z.lineType)) {
      betrag.set(z.key, { wert: 0n, zaehlt: false, ohne: true, satzBp })
      continue
    }
    if (z.lineType === 'SUBTOTAL') {
      betrag.set(z.key, { wert: 0n, zaehlt: false, ohne: false, satzBp: 0n })
      continue
    }
    if (z.lineType === 'DEDUCTION') {
      // Wird in Schritt 2b aus der Zwischensumme darüber gebildet.
      betrag.set(z.key, { wert: 0n, zaehlt: true, ohne: false, satzBp: 0n })
      continue
    }
    if (z.lineType === 'REFERENCE') {
      // Mahnung: die offene Rechnung als Zeile — ein Betrag ohne eigene Steuer.
      betrag.set(z.key, { wert: skaliert(z.unitPrice, 2), zaehlt: true, ohne: false, satzBp: 0n })
      continue
    }
    // ARTICLE
    if (elternteil && elternteil.subMode === 'OWN_PRICE') {
      // Kinder einer Pauschale sind Text: keine Beträge, zählen nie (5.3).
      betrag.set(z.key, { wert: 0n, zaehlt: false, ohne: true, satzBp })
      continue
    }
    if (kinder.length > 0 && (z.subMode ?? 'SUM_OF_CHILDREN') === 'SUM_OF_CHILDREN') {
      // Wird in Schritt 2 aus den Kindern gebildet.
      betrag.set(z.key, { wert: 0n, zaehlt: false, ohne: false, satzBp })
      continue
    }
    betrag.set(z.key, {
      wert: zeilenbetragCent(z.quantity, z.unitPrice, z.priceUnit, z.discountPercent),
      zaehlt: !nichtVerbindlich,
      ohne: false,
      satzBp,
    })
  }

  // 2. Hauptpositionen im Modus SUM_OF_CHILDREN: Anzeige der Kindersumme.
  for (const z of zeilen) {
    const kinder = kinderVon.get(z.key) ?? []
    if (z.lineType !== 'ARTICLE' || kinder.length === 0) continue
    if ((z.subMode ?? 'SUM_OF_CHILDREN') !== 'SUM_OF_CHILDREN') continue
    const summe = kinder.reduce((s, k) => {
      const b = betrag.get(k.key)!
      // Optionale Kinder gehören nicht in die Gruppensumme.
      return k.isOptional || k.isAlternative ? s : s + b.wert
    }, 0n)
    betrag.set(z.key, { ...betrag.get(z.key)!, wert: summe })
  }

  // 2b. Abzüge (Nachtrag): auf die Zwischensumme aller zählenden Zeilen darüber —
  // prozentual oder pauschal —, verteilt im Verhältnis der Steuersätze dieser
  // Zwischensumme (größter Rest), damit die Steuer je Satz richtig sinkt.
  const lauf = new Map<string, { satzBp: bigint; wert: bigint }>()
  const abzugTeile = new Map<string, { satzBp: bigint; teil: bigint }[]>()
  const abzugBasis = new Map<string, bigint>()
  for (const z of zeilen) {
    const b = betrag.get(z.key)!
    if (z.lineType === 'DEDUCTION') {
      const saetze = [...lauf.values()].sort((a, c) => Number(a.satzBp - c.satzBp))
      const basisCent = saetze.reduce((s, g) => s + g.wert, 0n)
      // Ein pauschaler Abzug folgt dem Vorzeichen der Zwischensumme — im Storno und in der
      // Gutschrift (negative Mengen) wirkt er so in dieselbe Richtung wie ein prozentualer.
      const abzugCent =
        z.discountPercent !== null && z.discountPercent !== undefined
          ? teileGerundet(basisCent * skaliert(z.discountPercent, 2), 10000n)
          : skaliert(Math.abs(z.unitPrice), 2) * (basisCent < 0n ? -1n : 1n)
      const teile = verteile(abzugCent, saetze.map((g) => g.wert))
      const liste = saetze.map((g, i) => ({ satzBp: g.satzBp, teil: teile[i] }))
      for (const t of liste) lauf.get(t.satzBp.toString())!.wert -= t.teil
      abzugTeile.set(z.key, liste)
      abzugBasis.set(z.key, basisCent)
      // Ohne zählende Zeilen darüber gibt es nichts, worauf der Abzug wirken könnte: 0.
      betrag.set(z.key, { ...b, wert: -liste.reduce((s, t) => s + t.teil, 0n) })
      continue
    }
    if (!b.zaehlt || z.lineType === 'REFERENCE' || z.lineType === 'SUBTOTAL') continue
    const k = b.satzBp.toString()
    lauf.set(k, { satzBp: b.satzBp, wert: (lauf.get(k)?.wert ?? 0n) + b.wert })
  }

  // 3. Zwischensummen (Nachtrag 4): alles Zählende **darüber**, Abzüge eingeschlossen —
  // dieselbe Zwischensumme, die ein Abzug zeigt. Bis dahin zählte sie nur seit der
  // letzten Zwischensumme; verschoben stand dann ein Betrag da, der zur Stelle nicht passte.
  let laufendeSumme = 0n
  for (const z of zeilen) {
    const b = betrag.get(z.key)!
    if (z.lineType === 'SUBTOTAL') {
      betrag.set(z.key, { ...b, wert: laufendeSumme })
    } else if (b.zaehlt) {
      laufendeSumme += b.wert
    }
  }

  // 4. Steuergruppen — je Satz auf die Summe, nicht je Zeile (7.3).
  const gruppen = new Map<string, { satzBp: bigint; wert: bigint }>()
  for (const z of zeilen) {
    const b = betrag.get(z.key)!
    if (z.lineType === 'DEDUCTION') {
      for (const t of abzugTeile.get(z.key) ?? []) {
        const k = t.satzBp.toString()
        gruppen.set(k, { satzBp: t.satzBp, wert: (gruppen.get(k)?.wert ?? 0n) - t.teil })
      }
      continue
    }
    if (!b.zaehlt || z.lineType === 'REFERENCE') continue
    const k = b.satzBp.toString()
    gruppen.set(k, { satzBp: b.satzBp, wert: (gruppen.get(k)?.wert ?? 0n) + b.wert })
  }
  const referenzSumme = zeilen
    .filter((z) => z.lineType === 'REFERENCE')
    .reduce((s, z) => s + betrag.get(z.key)!.wert, 0n)

  // Belegrabatt: auf die Summe der wertführenden Zeilen, anteilig auf die
  // Gruppen verteilt — mit dem größten Rest, damit die Teile die Summe ergeben.
  const rabattBp = skaliert(input.documentDiscountPercent ?? 0, 2)
  const gruppenListe = [...gruppen.values()].sort((a, b) => Number(a.satzBp - b.satzBp))
  const basis = gruppenListe.reduce((s, g) => s + g.wert, 0n)
  const rabattGesamt = teileGerundet(basis * rabattBp, 10000n)
  const anteile = verteile(rabattGesamt, gruppenListe.map((g) => g.wert))

  let netto = 0n
  let rabatt = 0n
  let steuer = 0n
  const taxes: CalcTaxGroup[] = gruppenListe.map((g, i) => {
    const nachRabatt = g.wert - anteile[i]
    let gruppenNetto: bigint
    let gruppenSteuer: bigint
    let gruppenRabatt: bigint
    if (brutto) {
      // Eingegeben ist brutto: erst den Rabatt, dann herausrechnen.
      gruppenNetto = nettoAusBrutto(nachRabatt, g.satzBp)
      gruppenSteuer = nachRabatt - gruppenNetto
      const vorRabattNetto = nettoAusBrutto(g.wert, g.satzBp)
      gruppenRabatt = vorRabattNetto - gruppenNetto
      netto += vorRabattNetto
    } else {
      gruppenNetto = nachRabatt
      gruppenSteuer = steuerAufNetto(nachRabatt, g.satzBp)
      gruppenRabatt = anteile[i]
      netto += g.wert
    }
    rabatt += gruppenRabatt
    steuer += gruppenSteuer
    return {
      taxRate: Number(g.satzBp) / 100,
      taxProcedure: input.taxProcedure,
      netAmount: centZuBetrag(gruppenNetto),
      taxAmount: centZuBetrag(gruppenSteuer),
    }
  })
  netto += referenzSumme

  // 5. Nummern und Zeilenwerte.
  let hauptNr = 0
  const unterNr = new Map<string, number>()
  // Die Hauptnummer je Zeile — von den Kindern gelesen. Lokal, damit zwei
  // Rechnungen nacheinander nichts voneinander wissen.
  const hauptNrVon = new Map<string, number>()
  const lines: CalcLineResult[] = zeilen.map((z) => {
    const b = betrag.get(z.key)!
    let positionNo = hauptNr
    let subPositionNo: number | null = null
    const nummeriert = z.lineType === 'ARTICLE' || z.lineType === 'REFERENCE'
    if (z.parentKey) {
      positionNo = hauptNrVon.get(z.parentKey) ?? hauptNr
      if (nummeriert) {
        const n = (unterNr.get(z.parentKey) ?? 0) + 1
        unterNr.set(z.parentKey, n)
        subPositionNo = n
      }
    } else if (nummeriert) {
      hauptNr += 1
      positionNo = hauptNr
    }
    hauptNrVon.set(z.key, positionNo)

    let lineNet: bigint
    let lineTax: bigint
    let lineGross: bigint
    if (b.ohne) {
      lineNet = 0n
      lineTax = 0n
      lineGross = 0n
    } else if (z.lineType === 'DEDUCTION') {
      // Steuer je Anteil — die Summe der Anteile ist der Zeilenbetrag.
      const teile = abzugTeile.get(z.key) ?? []
      if (brutto) {
        lineGross = b.wert
        lineNet = -teile.reduce((s, t) => s + nettoAusBrutto(t.teil, t.satzBp), 0n)
        lineTax = lineGross - lineNet
      } else {
        lineNet = b.wert
        lineTax = -teile.reduce((s, t) => s + steuerAufNetto(t.teil, t.satzBp), 0n)
        lineGross = lineNet + lineTax
      }
    } else if (brutto) {
      lineGross = b.wert
      lineNet = nettoAusBrutto(b.wert, b.satzBp)
      lineTax = lineGross - lineNet
    } else {
      lineNet = b.wert
      lineTax = steuerAufNetto(b.wert, b.satzBp)
      lineGross = lineNet + lineTax
    }
    return {
      key: z.key,
      positionNo,
      subPositionNo,
      countsToTotal: b.zaehlt,
      effectiveTaxRate: Number(b.satzBp) / 100,
      lineNet: centZuBetrag(lineNet),
      lineTax: centZuBetrag(lineTax),
      lineGross: centZuBetrag(lineGross),
      withoutAmounts: b.ohne,
      deductionBase: abzugBasis.has(z.key) ? centZuBetrag(abzugBasis.get(z.key)!) : null,
    }
  })

  return {
    lines,
    taxes,
    netTotal: centZuBetrag(netto),
    discountTotal: centZuBetrag(rabatt),
    taxTotal: centZuBetrag(steuer),
    grossTotal: centZuBetrag(netto - rabatt + steuer),
  }
}

/**
 * Verteilt `gesamt` im Verhältnis der `gewichte` — die Teile ergeben **genau**
 * `gesamt` (Verfahren des größten Rests).
 */
export function verteile(gesamt: bigint, gewichte: readonly bigint[]): bigint[] {
  const summe = gewichte.reduce((s, g) => s + g, 0n)
  if (summe === 0n || gesamt === 0n) return gewichte.map(() => 0n)
  const roh = gewichte.map((g) => ({ ganz: (gesamt * g) / summe, rest: (gesamt * g) % summe }))
  let offen = gesamt - roh.reduce((s, r) => s + r.ganz, 0n)
  const reihenfolge = roh
    .map((r, i) => ({ i, rest: r.rest < 0n ? -r.rest : r.rest }))
    .sort((a, b) => (a.rest === b.rest ? a.i - b.i : a.rest > b.rest ? -1 : 1))
  const ergebnis = roh.map((r) => r.ganz)
  const schritt = offen < 0n ? -1n : 1n
  for (const { i } of reihenfolge) {
    if (offen === 0n) break
    ergebnis[i] += schritt
    offen -= schritt
  }
  return ergebnis
}

// ─── Abflachung für die E-Rechnung (AP-32 5.5) ───────────────────────────────

export interface FlatLineInput extends CalcLineInput {
  name: string
  description: string | null
  unitCode: string | null
}

export interface FlatInvoiceLine {
  name: string
  description: string | null
  quantity: number
  unitCode: string | null
  unitPrice: number
  priceUnit: number
  lineNet: number
  taxRate: number
}

/**
 * > **Wertführende Zeile = E-Rechnungszeile. Alles Beschreibende wandert in deren
 * > Beschreibungstext.**
 *
 * - `OWN_PRICE`: Die Hauptposition ist die eine Zeile; ihre Kinder werden ihrer
 *   Beschreibung angehängt („Bestehend aus: 2 Stk Steher · 5 m² Verglasung").
 * - `SUM_OF_CHILDREN`: Die Kinder sind die Zeilen; der Text der Hauptposition
 *   wird jedem Kind vorangestellt („Terrassenüberdachung — Steher").
 *
 * Verlustfrei im Betrag und in der Steuer — getestet gegen `berechneBeleg`.
 */
export function flacheFuerERechnung(lines: readonly FlatLineInput[], ergebnis: CalcResult): FlatInvoiceLine[] {
  const nachSchluessel = new Map(ergebnis.lines.map((l) => [l.key, l]))
  const eltern = new Map(lines.map((l) => [l.key, l]))
  const kinderVon = new Map<string, FlatLineInput[]>()
  for (const l of lines) if (l.parentKey) kinderVon.set(l.parentKey, [...(kinderVon.get(l.parentKey) ?? []), l])

  const flach: FlatInvoiceLine[] = []
  for (const l of lines) {
    const r = nachSchluessel.get(l.key)!
    if (!r.countsToTotal || l.lineType !== 'ARTICLE') continue
    const elternteil = l.parentKey ? eltern.get(l.parentKey) : undefined
    let name = l.name
    let description = l.description
    if (elternteil) name = `${elternteil.name} — ${l.name}`
    const kinder = kinderVon.get(l.key) ?? []
    if (kinder.length > 0) {
      const teile = kinder.map((k) => [formatMenge(k.quantity), k.unitCode, k.name].filter(Boolean).join(' ')).join(' · ')
      description = [description, `Bestehend aus: ${teile}`].filter(Boolean).join('\n')
    }
    flach.push({
      name,
      description,
      quantity: l.quantity,
      unitCode: l.unitCode,
      unitPrice: l.unitPrice,
      priceUnit: l.priceUnit,
      lineNet: r.lineNet,
      taxRate: r.effectiveTaxRate,
    })
  }
  return flach
}

function formatMenge(menge: number): string {
  return Number.isInteger(menge) ? String(menge) : String(menge).replace('.', ',')
}
